import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Palette,
  Upload,
  RotateCcw,
  RefreshCw,
  Save,
  X,
  Image,
  Building2,
  Inbox,
  ShoppingCart,
  Globe,
  BarChart3,
  HelpCircle,
  Info,
  Plus,
  Trash2,
  Lock,
  Flag,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  useFeatureFlags, 
  useGlobalFeatureFlags, 
  useToggleFeatureFlag, 
  useAddFeatureFlag, 
  useRemoveFeatureFlag,
  type FeatureFlag 
} from "@/hooks/useFeatureFlags";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

// Client interface from database
interface Client {
  id: number;
  name: string;
  logo: string;
  status: string;
  planTier: string;
  onboardedDate: string;
  totalUsers: number;
  revenue: number;
}

// Branding data interface from API
interface BrandingData {
  logo: string;
  primaryColor: string;
  accentColor: string;
  customCss: string;
}

// Tenant interface with customization data
interface TenantCustomization {
  id: number;
  clientId: number;
  primaryColor: string;
  secondaryColor: string;
  customCSS?: string;
  features: {
    activityRequests: boolean;
    marketplaceAccess: boolean;
    multiLanguage: boolean;
    analytics: boolean;
    supportTicketing: boolean;
  };
}

// Default tenant customization
const getDefaultCustomization = (clientId: number): TenantCustomization => ({
  id: clientId,
  clientId,
  primaryColor: "#0EA5E9",
  secondaryColor: "#0369A1",
  customCSS: "",
  features: {
    activityRequests: true,
    marketplaceAccess: true,
    multiLanguage: true,
    analytics: true,
    supportTicketing: true
  }
});

export default function TenantCustomizations() {
  const { toast } = useToast();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [customizations, setCustomizations] = useState<Map<number, TenantCustomization>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(["branding", "features"]);
  const [showAddFlagModal, setShowAddFlagModal] = useState(false);
  const [selectedNewFlag, setSelectedNewFlag] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Fetch clients from database
  const { data: clients = [], isLoading } = useQuery<Client[]>({
    queryKey: ['/api/corporate-clients-list'],
    staleTime: 30000
  });

  // Fetch branding data for selected client
  const { data: brandingData } = useQuery<BrandingData>({
    queryKey: [`/api/clients/${selectedClientId}/branding`],
    enabled: !!selectedClientId,
  });

  // Feature Flags hooks
  const { data: featureFlags = [], isLoading: isLoadingFlags } = useFeatureFlags(
    selectedClientId ? selectedClientId.toString() : null
  );
  const { data: globalFlags = [] } = useGlobalFeatureFlags();
  const toggleFlag = useToggleFeatureFlag(selectedClientId?.toString() || '');
  const addFlag = useAddFeatureFlag(selectedClientId?.toString() || '');
  const removeFlag = useRemoveFeatureFlag(selectedClientId?.toString() || '');
  
  // Initialize selected client and customizations when data loads
  useEffect(() => {
    if (clients.length > 0 && !selectedClientId) {
      const firstClient = clients[0];
      setSelectedClientId(firstClient.id);
      setSelectedClient(firstClient);
      
      // Initialize customizations for all clients
      const newCustomizations = new Map<number, TenantCustomization>();
      clients.forEach(client => {
        newCustomizations.set(client.id, getDefaultCustomization(client.id));
      });
      setCustomizations(newCustomizations);
    }
  }, [clients, selectedClientId]);
  
  const currentCustomization = selectedClientId ? customizations.get(selectedClientId) : null;
  
  // Local state for editable fields
  const [brandingConfig, setBrandingConfig] = useState({
    primaryColor: currentCustomization?.primaryColor || "#0EA5E9",
    secondaryColor: currentCustomization?.secondaryColor || "#0369A1",
    logo: selectedClient?.logo || "",
    customCSS: currentCustomization?.customCSS || ""
  });
  
  // Feature toggles are now managed through the API

  // Update local state when client selection changes or branding data is fetched
  useEffect(() => {
    if (brandingData && selectedClient) {
      setBrandingConfig({
        primaryColor: brandingData.primaryColor || "#0EA5E9",
        secondaryColor: brandingData.accentColor || "#0369A1",
        logo: brandingData.logo || "",
        customCSS: brandingData.customCss || ""
      });
      setHasChanges(false);
    } else if (currentCustomization && selectedClient) {
      setBrandingConfig({
        primaryColor: currentCustomization.primaryColor,
        secondaryColor: currentCustomization.secondaryColor,
        logo: selectedClient.logo || "",
        customCSS: currentCustomization.customCSS || ""
      });
      setHasChanges(false);
    }
  }, [selectedClientId, currentCustomization, selectedClient, brandingData]);

  // Check for changes
  useEffect(() => {
    if (!currentCustomization) return;
    
    const brandingChanged = 
      brandingConfig.primaryColor !== currentCustomization.primaryColor ||
      brandingConfig.secondaryColor !== currentCustomization.secondaryColor ||
      brandingConfig.logo !== (selectedClient?.logo || "") ||
      brandingConfig.customCSS !== currentCustomization.customCSS;
      
    setHasChanges(brandingChanged);
  }, [brandingConfig, currentCustomization, selectedClient]);

  // API Mutations
  const updateBrandingMutation = useMutation({
    mutationFn: async (data: { primaryColor: string; accentColor: string }) => {
      if (!selectedClientId) throw new Error('No client selected');
      return apiRequest('PUT', `/api/clients/${selectedClientId}/branding`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${selectedClientId}/branding`] });
    },
  });

  const updateCssMutation = useMutation({
    mutationFn: async (css: string) => {
      if (!selectedClientId) throw new Error('No client selected');
      return apiRequest('PUT', `/api/clients/${selectedClientId}/css`, { css });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${selectedClientId}/branding`] });
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!selectedClientId) throw new Error('No client selected');
      const formData = new FormData();
      formData.append('logo', file);
      
      return apiRequest('POST', `/api/clients/${selectedClientId}/logo`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${selectedClientId}/branding`] });
    },
  });

  const handleSave = async () => {
    try {
      // Update branding colors
      await updateBrandingMutation.mutateAsync({
        primaryColor: brandingConfig.primaryColor,
        accentColor: brandingConfig.secondaryColor
      });

      // Update custom CSS if changed
      if (brandingConfig.customCSS !== currentCustomization?.customCSS) {
        await updateCssMutation.mutateAsync(brandingConfig.customCSS);
      }

      toast({
        title: "Customizations saved",
        description: "Your changes have been applied successfully.",
      });
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error saving customizations",
        description: "Failed to save your changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDiscard = () => {
    if (currentCustomization && selectedClient) {
      setBrandingConfig({
        primaryColor: currentCustomization.primaryColor,
        secondaryColor: currentCustomization.secondaryColor,
        logo: selectedClient.logo || "",
        customCSS: currentCustomization.customCSS || ""
      });
      setHasChanges(false);
    }
  };

  const handleResetBranding = () => {
    setBrandingConfig({
      primaryColor: "#0EA5E9",
      secondaryColor: "#0369A1",
      logo: "",
      customCSS: ""
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const result = await uploadLogoMutation.mutateAsync(file);
        setBrandingConfig(prev => ({ ...prev, logo: result.logo }));
        toast({
          title: "Logo uploaded",
          description: "Your logo has been uploaded successfully.",
        });
      } catch (error) {
        toast({
          title: "Upload failed",
          description: "Failed to upload logo. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const result = await uploadLogoMutation.mutateAsync(file);
        setBrandingConfig(prev => ({ ...prev, logo: result.logo }));
        toast({
          title: "Logo uploaded",
          description: "Your logo has been uploaded successfully.",
        });
      } catch (error) {
        toast({
          title: "Upload failed",
          description: "Failed to upload logo. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header 
          title="Tenant Customizations"
          description="Configure branding and feature toggles per client"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading tenants...</p>
          </div>
        </div>
      </div>
    );
  }

  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    const isMobile = window.innerWidth <= 600;
    
    if (isMobile) {
      return (
        <Accordion 
          type="multiple" 
          value={expandedSections} 
          onValueChange={setExpandedSections}
          className="space-y-4"
        >
          {children}
        </Accordion>
      );
    }
    
    return <div className="space-y-6">{children}</div>;
  };

  const BrandingSection = () => {
    const content = (
      <>
        {/* Color Pickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <Label htmlFor="primary-color" className="text-sm font-medium">Primary Color</Label>
            <div className="flex items-center gap-3 mt-2">
              <input
                id="primary-color"
                type="color"
                value={brandingConfig.primaryColor}
                onChange={(e) => setBrandingConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-12 h-10 rounded border border-input cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Choose primary color"
              />
              <Input
                value={brandingConfig.primaryColor}
                onChange={(e) => setBrandingConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                placeholder="#0EA5E9"
                className="font-mono"
                aria-label="Primary color hex value"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="secondary-color" className="text-sm font-medium">Secondary Color</Label>
            <div className="flex items-center gap-3 mt-2">
              <input
                id="secondary-color"
                type="color"
                value={brandingConfig.secondaryColor}
                onChange={(e) => setBrandingConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                className="w-12 h-10 rounded border border-input cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Choose secondary color"
              />
              <Input
                value={brandingConfig.secondaryColor}
                onChange={(e) => setBrandingConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                placeholder="#0369A1"
                className="font-mono"
                aria-label="Secondary color hex value"
              />
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="mb-6">
          <Label className="text-sm font-medium">Logo Upload</Label>
          <div 
            className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {brandingConfig.logo ? (
              <div className="space-y-3">
                <img 
                  src={brandingConfig.logo} 
                  alt="Tenant logo" 
                  className="w-16 h-16 mx-auto object-contain"
                />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setBrandingConfig(prev => ({ ...prev, logo: "" }))}
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove Logo
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Drag and drop or click to upload</p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG up to 2MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  aria-label="Upload logo file"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose File
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Custom CSS */}
        <div>
          <Label htmlFor="custom-css" className="text-sm font-medium">Custom CSS</Label>
          <Textarea
            id="custom-css"
            value={brandingConfig.customCSS}
            onChange={(e) => setBrandingConfig(prev => ({ ...prev, customCSS: e.target.value }))}
            placeholder="/* Enter custom CSS styles here */"
            className="font-mono min-h-[120px] mt-2"
            aria-label="Custom CSS code"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button 
            onClick={handleSave}
            disabled={!hasChanges}
            className="athlyn-button-primary"
          >
            Save Branding
          </Button>
          <Button 
            variant="outline"
            onClick={handleResetBranding}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Default
          </Button>
        </div>
      </>
    );

    const isMobile = window.innerWidth <= 600;
    
    if (isMobile) {
      return (
        <AccordionItem value="branding">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Branding Overrides
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="athlyn-card">
              <CardContent className="pt-6">{content}</CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
      );
    }

    return (
      <Card className="athlyn-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Branding Overrides
          </CardTitle>
        </CardHeader>
        <CardContent>{content}</CardContent>
      </Card>
    );
  };

  const FeatureTogglesSection = () => {
    const handleToggleFeature = async (flag: FeatureFlag) => {
      if (flag.locked) {
        toast({
          title: "Feature Locked",
          description: "This is an admin-only feature and cannot be toggled.",
          variant: "destructive"
        });
        return;
      }
      
      await toggleFlag.mutateAsync({
        flagKey: flag.key,
        enabled: !flag.enabled
      });
    };

    const handleAddFlag = async () => {
      if (!selectedNewFlag) {
        toast({
          title: "Select a Flag",
          description: "Please select a feature flag to add.",
          variant: "destructive"
        });
        return;
      }

      await addFlag.mutateAsync(selectedNewFlag);
      setShowAddFlagModal(false);
      setSelectedNewFlag("");
    };

    const content = (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Feature Flags</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddFlagModal(true)}
            disabled={!selectedClientId}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Flag
          </Button>
        </div>

        {isLoadingFlags ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : featureFlags.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Flag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No feature flags assigned to this client.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddFlagModal(true)}
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Flag
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {featureFlags.map((flag) => (
              <div 
                key={flag.key}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start gap-3 flex-1">
                  <Flag className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Label 
                        htmlFor={`toggle-${flag.key}`}
                        className="text-base font-medium cursor-pointer"
                      >
                        {flag.name}
                      </Label>
                      {flag.locked && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Lock className="w-4 h-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Admin-only feature</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {flag.category}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{flag.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`toggle-${flag.key}`}
                    checked={flag.enabled || false}
                    onCheckedChange={() => handleToggleFeature(flag)}
                    disabled={flag.locked || toggleFlag.isPending}
                    aria-label={`Toggle ${flag.name}`}
                  />
                  {!flag.locked && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFlag.mutate(flag.key)}
                      disabled={removeFlag.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    const isMobile = window.innerWidth <= 600;
    
    if (isMobile) {
      return (
        <AccordionItem value="features">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Feature Flags
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="athlyn-card">
              <CardContent className="pt-6">{content}</CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
      );
    }

    return (
      <Card className="athlyn-card">
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
        </CardHeader>
        <CardContent>{content}</CardContent>
      </Card>
    );
  };

  const PreviewPanel = () => {
    const content = (
      <div className="space-y-4">
        {/* Mock Dashboard Header */}
        <div 
          className="p-4 rounded-lg shadow-sm border"
          style={{ 
            backgroundColor: brandingConfig.primaryColor + '10',
            borderColor: brandingConfig.primaryColor 
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {brandingConfig.logo ? (
                <img 
                  src={brandingConfig.logo} 
                  alt="Preview logo" 
                  className="w-10 h-10 object-contain"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: brandingConfig.primaryColor }}
                >
                  {selectedClient?.name.charAt(0)}
                </div>
              )}
              <div>
                <h3 className="font-semibold" style={{ color: brandingConfig.primaryColor }}>
                  {selectedClient?.name}
                </h3>
                <p className="text-sm text-muted-foreground">Dashboard Preview</p>
              </div>
            </div>
            <Button 
              size="sm" 
              style={{ 
                backgroundColor: brandingConfig.secondaryColor,
                color: 'white'
              }}
            >
              Action Button
            </Button>
          </div>
        </div>

        {/* Feature Indicators */}
        <div className="grid grid-cols-2 gap-2">
          {featureFlags.filter(flag => flag.enabled).map((flag) => (
            <div key={flag.key} className="text-xs px-2 py-1 bg-muted rounded">
              ✓ {flag.name}
            </div>
          ))}
        </div>

        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => console.log('Refresh preview')}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Preview
        </Button>
      </div>
    );

    const isMobile = window.innerWidth <= 600;
    
    if (isMobile) {
      return (
        <AccordionItem value="preview">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Preview Panel
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="athlyn-card">
              <CardContent className="pt-6">{content}</CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
      );
    }

    return (
      <Card className="athlyn-card">
        <CardHeader>
          <CardTitle>Preview Panel</CardTitle>
        </CardHeader>
        <CardContent>{content}</CardContent>
      </Card>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <Header 
        title="Tenant Customizations"
        description="Configure branding and feature toggles per client"
      />
      
      <div className="flex-1 overflow-auto p-6">
        {/* Client/Tenant Selector */}
        <div className="mb-6">
          <Label htmlFor="client-select" className="text-sm font-medium mb-2 block">
            Select Client/Tenant
          </Label>
          <Select 
            value={selectedClientId?.toString()} 
            onValueChange={(value) => {
              const clientId = parseInt(value);
              const client = clients.find(c => c.id === clientId);
              setSelectedClientId(clientId);
              setSelectedClient(client || null);
            }}
          >
            <SelectTrigger id="client-select" className="w-full md:w-80">
              <SelectValue placeholder="Choose a client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id.toString()}>
                  <div className="flex items-center gap-2">
                    {client.logo ? (
                      <img src={client.logo} alt="" className="w-5 h-5 rounded" />
                    ) : (
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span>{client.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({client.planTier})
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ContentWrapper>
              <BrandingSection />
              <FeatureTogglesSection />
            </ContentWrapper>
          </div>
          
          <div className="lg:col-span-1">
            <ContentWrapper>
              <PreviewPanel />
            </ContentWrapper>
          </div>
        </div>
      </div>

      {/* Sticky Footer Actions */}
      <div className="border-t bg-background p-4">
        <div className="flex justify-end gap-3">
          <Button 
            variant="ghost" 
            onClick={handleDiscard}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!hasChanges}
            className="athlyn-button-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            Save All Customizations
          </Button>
        </div>
      </div>

      {/* Add Flag Modal */}
      <Dialog open={showAddFlagModal} onOpenChange={setShowAddFlagModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Feature Flag</DialogTitle>
            <DialogDescription>
              Select a feature flag to add to {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="max-h-96 overflow-y-auto space-y-2">
              {globalFlags
                .filter(globalFlag => !featureFlags.some(f => f.key === globalFlag.key))
                .map((flag) => (
                  <div 
                    key={flag.key}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedNewFlag === flag.key 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:bg-muted/30'
                    }`}
                    onClick={() => setSelectedNewFlag(flag.key)}
                  >
                    <div className="flex items-start gap-3">
                      <Flag className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{flag.name}</h4>
                          {flag.locked && (
                            <Lock className="w-4 h-4 text-muted-foreground" />
                          )}
                          <Badge variant="outline" className="text-xs">
                            {flag.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {flag.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            
            {globalFlags.filter(globalFlag => 
              !featureFlags.some(f => f.key === globalFlag.key)
            ).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>All available flags have been assigned to this client.</p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddFlagModal(false);
                  setSelectedNewFlag("");
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  if (!selectedNewFlag) {
                    toast({
                      title: "Select a Flag",
                      description: "Please select a feature flag to add.",
                      variant: "destructive"
                    });
                    return;
                  }

                  await addFlag.mutateAsync(selectedNewFlag);
                  setShowAddFlagModal(false);
                  setSelectedNewFlag("");
                }}
                disabled={!selectedNewFlag || addFlag.isPending}
              >
                {addFlag.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Flag
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}